/**
 * @file THE PLAYER'S MONEY — one number, and the four things that may be done
 * to it.
 *
 * There was no money in this game until the shops arrived, so this file is
 * deliberately the smallest thing that can be the single source of truth for
 * one: a closure over an integer, a clamp at each end, and a bus channel the
 * HUD subscribes to. Missions pay into it, the till spends out of it, and
 * nobody else may hold a second copy of the figure — a HUD that keeps its own
 * running total is a HUD that drifts the first time a purchase is refused.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO ENGINE, NO GAME
 * ─────────────────────────────────────────────────────────────────────────────
 * Same mould as `systems/Interiors.js` and `systems/Carjack.js`: the bus is
 * injected, nothing is imported, and every branch is provable in node. It is
 * also the only system here with no `update` — money does not tick.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **`'player:cash'` fires on CONSTRUCTION.** `ui/hud/CashBar.jsx` renders
 *   `null` until its first event (the `HealthBar` idiom), so a wallet that only
 *   spoke when the figure moved would leave the HUD blank until the player's
 *   first purchase. The channel is low-rate by nature — a sale, a mission
 *   payout — so it is emitted raw and the subscriber throttles if it cares.
 *
 * • **A REFUSAL IS SILENT.** `spend()` returning false emits nothing and
 *   changes nothing, so a caller that ignores the return value cannot put the
 *   player into debt or make the HUD flicker a value that was never real. The
 *   prompt is what tells the player they are short; see `systems/Shops.js`.
 *
 * @module engine/systems/Wallet
 */

/**
 * The two limits. `start` is the new-game float — enough for a few snacks and
 * one set of body armour, so the first shop the player walks into is a real
 * choice rather than a locked door.
 */
export const WALLET = Object.freeze({
  /** What a fresh player carries. */
  start: 500,
  /**
   * The ceiling. Seven digits keeps `$9,999,999` inside the HUD's cash slot at
   * every font size, and it is far past anything the mission table can pay.
   */
  max: 9_999_999,
});

/**
 * @param {number} n
 * @returns {boolean} A real, usable amount of money.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * The player's money.
 *
 * @param {Object} [o]
 * @param {any} [o.bus] `'player:cash'` `{cash}` — on construction and on every
 *   change, never on a refusal.
 * @param {number} [o.start] Opening balance. Clamped into `[0, WALLET.max]`; a
 *   non-finite one falls back to {@link WALLET}`.start` rather than poisoning
 *   the arithmetic with `NaN`.
 * @returns {Object}
 */
export function createWallet(o = {}) {
  const { bus = null, start = WALLET.start } = o ?? {};

  /** THE figure. Never negative, never above {@link WALLET}`.max`. */
  let cash = usable(start) ? Math.min(WALLET.max, Math.max(0, Math.floor(start))) : WALLET.start;
  /** Lifetime totals, for `stats()` and for the suites. */
  let earned = 0;
  let spent = 0;
  let disposed = false;

  const publish = () => {
    bus?.emit?.('player:cash', { cash });
  };

  // The HUD's first paint depends on this — see the header.
  publish();

  return {
    name: 'wallet',

    /** @returns {number} The balance. Always an integer >= 0. */
    get balance() {
      return cash;
    },

    /**
     * Pays money IN — a mission reward, a found roll of notes.
     * @param {number} n Ignored unless it is a finite number > 0: `add` is not
     *   a back door into {@link spend}, and a `NaN` reward from a malformed
     *   mission def must not erase the player's money.
     * @returns {number} The new balance.
     */
    add(n) {
      if (disposed || !usable(n) || n <= 0) return cash;
      const next = Math.min(WALLET.max, cash + Math.floor(n));
      if (next === cash) return cash; // already at the ceiling: no change, no emit
      earned += next - cash;
      cash = next;
      publish();
      return cash;
    },

    /**
     * Takes money OUT, all or nothing.
     * @param {number} n Price. A negative or non-finite one is REFUSED rather
     *   than treated as income.
     * @returns {boolean} True when the money left the wallet. False changes
     *   nothing and emits nothing — see the header.
     */
    spend(n) {
      if (disposed || !usable(n) || n < 0) return false;
      const amt = Math.floor(n);
      if (amt > cash) return false;
      if (amt === 0) return true; // free is affordable, but it is not a change
      cash -= amt;
      spent += amt;
      publish();
      return true;
    },

    /**
     * @param {number} n
     * @returns {boolean} {@link spend} would succeed. The till asks this to
     *   decide between offering the item and offering the price, so the two
     *   MUST agree — they share this one test rather than each doing the
     *   comparison.
     */
    canAfford(n) {
      if (disposed || !usable(n) || n < 0) return false;
      return Math.floor(n) <= cash;
    },

    /** @returns {{cash: number, earned: number, spent: number, max: number}} */
    stats() {
      return { cash, earned, spent, max: WALLET.max };
    },

    /**
     * Idempotent. Freezes the figure: every mutator is a no-op afterwards and
     * nothing more is published, so a system torn down mid-frame cannot repaint
     * a HUD that is on its way out.
     */
    dispose() {
      disposed = true;
    },
  };
}
